import { Checkbox, Dialog, useToast } from '@ordr-as/umami-react'
import { useState, useMemo } from 'react'
import { CountryCode, FavritApp, PaymentProvider } from '../../generated-api'
import { useCreatePaymentMethodCountryConnection, usePaymentMethodCountries } from 'hooks/usePaymentMethodApi'
import { mapCountryCodeToCountryName } from 'utils/countryUtils'
import { mapFavritAppToName } from 'utils/favritAppUtils'

type AddCountryDialogProps = {
    paymentProvider: PaymentProvider
    paymentMethod: string
    dialogIsOpen: boolean
    onClose: () => void
}

const AddCountryDialog = (props: AddCountryDialogProps) => {
    const { paymentProvider, paymentMethod, dialogIsOpen, onClose } = props

    const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode | undefined>(undefined)
    const [paymentsEnabled, setPaymentsEnabled] = useState(true)
    const [enabledInApps, setEnabledInApps] = useState<Set<FavritApp>>(new Set([FavritApp.ADMIN_APP]))

    const toast = useToast()

    const { data: existingCountries } = usePaymentMethodCountries(paymentProvider, paymentMethod)

    const { mutate: createCountryConnection, isLoading: isCreating } = useCreatePaymentMethodCountryConnection(
        paymentProvider,
        paymentMethod,
        selectedCountryCode as CountryCode
    )

    // Get available countries (not already connected)
    const availableCountries = useMemo(() => {
        const allCountries = Object.values(CountryCode)
        const connectedCountryCodes = new Set(existingCountries?.map((c) => c.countryCode) || [])
        return allCountries.filter((cc) => !connectedCountryCodes.has(cc))
    }, [existingCountries])

    const handleAppToggle = (app: FavritApp) => {
        const newEnabledInApps = new Set(enabledInApps)
        if (newEnabledInApps.has(app)) {
            newEnabledInApps.delete(app)
        } else {
            newEnabledInApps.add(app)
        }
        setEnabledInApps(newEnabledInApps)
    }

    const handleAdd = () => {
        if (!selectedCountryCode) {
            toast.pushError({
                id: 'add-country-no-selection',
                message: 'Please select a country',
            })
            return
        }

        createCountryConnection(
            {
                paymentsEnabled,
                defaultEnabledInApps: enabledInApps,
            },
            {
                onSuccess: () => {
                    toast.pushSuccess({
                        id: 'add-country-success',
                        message: `Successfully added ${mapCountryCodeToCountryName(selectedCountryCode)}`,
                    })
                    handleClose()
                },
                onError: (error) => {
                    console.error('Failed to add country:', error)
                    toast.pushError({
                        id: 'add-country-error',
                        message: 'Failed to add country to payment method',
                    })
                },
            }
        )
    }

    const handleClose = () => {
        setSelectedCountryCode(undefined)
        setPaymentsEnabled(true)
        setEnabledInApps(new Set([FavritApp.ADMIN_APP]))
        onClose()
    }

    return (
        <>
            {dialogIsOpen && (
                <Dialog
                    title="Add Country"
                    onClose={handleClose}
                    actionLabel={isCreating ? 'Adding...' : 'Add'}
                    onAction={handleAdd}
                    secondaryActionLabel="Cancel"
                    onSecondaryAction={handleClose}
                    isDisabledAction={!selectedCountryCode || isCreating}
                    isDisabledSecondaryAction={isCreating}
                >
                    <div className="flex flex-col gap-4">
                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Select Country *
                            </label>
                            {availableCountries.length === 0 ? (
                                <div className="text-grey-dark">All countries are already connected</div>
                            ) : (
                                <select
                                    className="w-full border border-grey-light rounded p-2"
                                    value={selectedCountryCode || ''}
                                    onChange={(e) => setSelectedCountryCode(e.target.value as CountryCode)}
                                >
                                    <option value="">-- Select a country --</option>
                                    {availableCountries.map((cc) => (
                                        <option key={cc} value={cc}>
                                            {mapCountryCodeToCountryName(cc)} ({cc})
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        <div>
                            <Checkbox isSelected={paymentsEnabled} onChange={setPaymentsEnabled}>
                                Payments Enabled
                            </Checkbox>
                            <p className="text-small text-grey-dark mt-1">
                                If enabled, payments can be processed in this country
                            </p>
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">
                                Default Enabled In Apps *
                            </label>
                            <div className="flex flex-col gap-2">
                                {Object.values(FavritApp).map((app) => (
                                    <Checkbox
                                        key={app}
                                        isSelected={enabledInApps.has(app)}
                                        onChange={() => handleAppToggle(app)}
                                    >
                                        {mapFavritAppToName(app)}
                                    </Checkbox>
                                ))}
                            </div>
                            <p className="text-small text-grey-dark mt-1">
                                Default apps where this payment method will be visible for locations in this country
                            </p>
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    )
}

export default AddCountryDialog
